/**
 * Browser Notification Service
 * Handles push notifications for posting reminders
 */

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.supported = 'Notification' in window;
  }

  // Check if notifications are supported
  isSupported() {
    return this.supported;
  }

  // Get current permission status
  getPermission() {
    if (!this.supported) return 'unsupported';
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.supported) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (err) {
      console.error('Error requesting notification permission:', err);
      return false;
    }
  }

  // Send a notification
  send(title, options = {}) {
    if (!this.supported || Notification.permission !== 'granted') {
      console.warn('Notifications not permitted');
      return null;
    }

    const defaultOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      // Auto-close after 10 seconds if not required interaction
      if (!defaultOptions.requireInteraction) {
        setTimeout(() => notification.close(), 10000);
      }

      return notification;
    } catch (err) {
      console.error('Error sending notification:', err);
      return null;
    }
  }

  // Posting time reminder
  sendPostingReminder(platform, contentTitle) {
    const platformEmojis = {
      linkedin: '💼',
      twitter: '🐦',
      facebook: '📘',
      instagram: '📸',
      medium: '📝'
    };

    const emoji = platformEmojis[platform.toLowerCase()] || '📢';
    
    return this.send(`${emoji} Time to post on ${platform}!`, {
      body: `Your content "${contentTitle}" is ready. It's optimal posting time!`,
      tag: `posting-${platform}-${Date.now()}`,
      requireInteraction: true,
      actions: [
        { action: 'post', title: 'Post Now' },
        { action: 'snooze', title: 'Remind Later' }
      ]
    });
  }

  // Distribution complete notification
  sendDistributionComplete(platformCount) {
    return this.send('☀️ Apollo Distribution Complete!', {
      body: `Your content has been prepared for ${platformCount} platforms. Ready to publish!`,
      tag: 'distribution-complete'
    });
  }

  // Competitor alert notification
  sendCompetitorAlert(alertTitle) {
    return this.send('🚨 Competitor Alert from Athena', {
      body: alertTitle,
      tag: 'competitor-alert',
      requireInteraction: true
    });
  }

  // Content remix complete
  sendRemixComplete(style) {
    return this.send('🔄 Content Remix Ready!', {
      body: `Your ${style} remix is complete and ready to distribute.`,
      tag: 'remix-complete'
    });
  }

  // Schedule a notification for later
  scheduleNotification(title, options, delayMs) {
    return setTimeout(() => {
      this.send(title, options);
    }, delayMs);
  }

  // Schedule posting reminders based on Sun Dial times
  schedulePostingReminders(postingSchedule, contentTitle) {
    const scheduledIds = [];
    const now = new Date();

    Object.entries(postingSchedule).forEach(([platform, schedule]) => {
      if (schedule.optimal_time) {
        // Parse the optimal time (e.g., "10:00 AM")
        const [time, period] = schedule.optimal_time.split(' ');
        const [hours, minutes] = time.split(':').map(Number);
        
        let targetHour = hours;
        if (period === 'PM' && hours !== 12) targetHour += 12;
        if (period === 'AM' && hours === 12) targetHour = 0;

        const targetTime = new Date();
        targetTime.setHours(targetHour, minutes, 0, 0);

        // If time has passed today, schedule for tomorrow
        if (targetTime <= now) {
          targetTime.setDate(targetTime.getDate() + 1);
        }

        // Schedule 30 minutes before
        const reminderTime = new Date(targetTime.getTime() - 30 * 60 * 1000);
        const delayMs = reminderTime.getTime() - now.getTime();

        if (delayMs > 0) {
          const id = this.scheduleNotification(
            `⏰ Post to ${platform} in 30 minutes!`,
            {
              body: `"${contentTitle}" - Optimal time: ${schedule.optimal_time}`,
              tag: `reminder-${platform}`,
              requireInteraction: true
            },
            delayMs
          );
          scheduledIds.push({ platform, id, time: reminderTime });
        }
      }
    });

    return scheduledIds;
  }

  // Cancel a scheduled notification
  cancelScheduled(timeoutId) {
    clearTimeout(timeoutId);
  }

  // Cancel all scheduled notifications
  cancelAllScheduled(scheduledIds) {
    scheduledIds.forEach(({ id }) => clearTimeout(id));
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;
